// ─── Telegram → 🤖 Cosa dice il bot ai soci ───────────────────────────────────
//
// 🧭 Quello che il bot dei soci sa del circolo, e che **solo il circolo** può
// dirgli: il numero della segreteria, fin quando si prenota, quante ore prima si
// disdice, l'informativa, e le informazioni sul circolo scritte a parole.
//
// ⭐⭐ **PERCHÉ ESISTE.** La rotta c'era dal 22/09 (`/settings/consumer-kb`),
// la schermata no: il numero della segreteria si cambiava solo con una chiamata
// scritta a mano, e quindi **non si sarebbe cambiato mai**. Il bot lo avrebbe
// detto ai soci per anni.
// 📌 *Un dato che per cambiare ha bisogno di un tecnico è un dato che invecchia
//    senza che nessuno se ne accorga.*
//
// 🚨 **Il bot non inventa niente**: quello che qui è vuoto, il bot dice che non
// riesce a recuperarlo e manda in segreteria. Una casella vuota è una risposta
// onesta; un valore messo lì «tanto per» diventa una risposta falsa in chat.
//
// ⚠️ **Si salva tutto insieme**, perché la riga è una sola (`PUT`): quello che
// questa schermata non mostra da modificare — la scala dei livelli — si rimanda
// com'era, invece di perderlo.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ARGOMENTI_DEL_CIRCOLO,
  whatsappInternazionale,
  type ChiaveArgomento,
  type ConoscenzaSalvata,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'conoscenza-bot';

type Argomento = { testo: string; appunti: string };

type Modulo = {
  telefono: string;
  orari: string;
  whatsapp: string;
  informativa: string;
  giorni: string;
  ore: string;
  argomenti: Record<ChiaveArgomento, Argomento>;
};

function daSalvata(c: ConoscenzaSalvata): Modulo {
  const argomenti = Object.fromEntries(
    ARGOMENTI_DEL_CIRCOLO.map((a) => [
      a.chiave,
      { testo: c.informazioni?.[a.chiave]?.testo ?? '', appunti: c.informazioni?.[a.chiave]?.appunti ?? '' },
    ]),
  ) as Record<ChiaveArgomento, Argomento>;
  return {
    telefono: c.segreteria?.telefono ?? '',
    orari: c.segreteria?.orari ?? '',
    whatsapp: c.segreteria?.whatsapp ?? '',
    informativa: c.privacy?.informativaUrl ?? '',
    giorni: c.prenotabilita?.giorniDiAnticipoMax ? String(c.prenotabilita.giorniDiAnticipoMax) : '',
    ore: c.disdetta?.oreMinime ? String(c.disdetta.oreMinime) : '',
    argomenti,
  };
}

/** Vuoto ⇒ `null`: una casella svuotata vuol dire «non lo dichiariamo». */
function testoONull(s: string): string | null {
  const t = s.trim();
  return t ? t : null;
}

/** Un numero intero positivo, o `null` se la casella è vuota; `undefined` se è scritto storto. */
function interoPositivo(s: string): number | null | undefined {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export function BotSoci(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<ConoscenzaSalvata>('GET', '/settings/consumer-kb'),
  });

  if (letta.isLoading) return <Attesa cosa="quello che il bot sa del circolo" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return <Pagina salvata={letta.data} modificabile={modificabile} />;
}

function Pagina({
  salvata,
  modificabile,
}: {
  salvata: ConoscenzaSalvata;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [m, setM] = useState<Modulo>(() => daSalvata(salvata));
  const [salvato, setSalvato] = useState(false);
  // Se la riga cambia da un'altra parte (un'altra scheda aperta), si riparte da lei.
  useEffect(() => setM(daSalvata(salvata)), [salvata]);

  const giorni = interoPositivo(m.giorni);
  const ore = interoPositivo(m.ore);
  // 📄 Voce 37: non si scrive più qui; si rimanda quella letta, e il server tiene la sua.
  const informativa = testoONull(m.informativa);
  const whatsapp = testoONull(m.whatsapp);
  /*
   * 🚨 Senza prefisso del paese il gestionale non lo manda al bot
   *    (`whatsappInternazionale`): `wa.me` aprirebbe la chat con uno
   *    sconosciuto. Lo si dice qui, invece di lasciar sparire il bottone.
   */
  const whatsappStorto = whatsapp !== null && whatsappInternazionale(whatsapp) === null;
  const pronta = giorni !== undefined && ore !== undefined && !whatsappStorto;

  const salva = useMutation({
    meta: { fatto: 'testi del bot salvati: i soci li leggono da adesso' },
    mutationFn: () => {
      const corpo: ConoscenzaSalvata = {
        segreteria: { telefono: testoONull(m.telefono), orari: testoONull(m.orari), whatsapp },
        privacy: { informativaUrl: informativa },
        prenotabilita: { giorniDiAnticipoMax: giorni ?? null },
        disdetta: { oreMinime: ore ?? null },
        // ⚠️ Si rimanda com'era: questa schermata non la cambia.
        livelli: salvata.livelli ?? null,
        informazioni: Object.fromEntries(
          ARGOMENTI_DEL_CIRCOLO.map((a) => [
            a.chiave,
            {
              testo: testoONull(m.argomenti[a.chiave].testo),
              appunti: testoONull(m.argomenti[a.chiave].appunti),
            },
          ]),
        ),
      };
      return chiama<ConoscenzaSalvata>('PUT', '/settings/consumer-kb', corpo);
    },
    onSuccess: () => {
      setSalvato(true);
      rileggi();
    },
  });

  const cambia = (f: (x: Modulo) => Modulo) => {
    setSalvato(false);
    setM(f);
  };
  const argomento = (k: ChiaveArgomento, campo: keyof Argomento, v: string) =>
    cambia((x) => ({ ...x, argomenti: { ...x.argomenti, [k]: { ...x.argomenti[k], [campo]: v } } }));

  const fasce = salvata.livelli?.fasce ?? [];

  return (
    <section>
      <header className="testata">
        <h2>Cosa dice il bot ai soci</h2>
        <p className="tenue">
          Quello che il bot dice ai soci quando glielo chiedono. Una casella vuota non è un errore: il bot
          dice che non riesce a recuperare quell’informazione e manda in segreteria — non se la inventa.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      <form
        className="bot-soci"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronta && modificabile) salva.mutate();
        }}
      >
        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>La segreteria</h3>
          <Campo
            etichetta="Numero di telefono"
            inputMode="tel"
            placeholder="0422 123456"
            value={m.telefono}
            onChange={(e) => cambia((x) => ({ ...x, telefono: e.target.value }))}
          />
          <p className="tenue piccolo">
            È il numero che il bot scrive ogni volta che manda qualcuno in segreteria. 🚨 Gli unici numeri di
            telefono che il bot può dare sono questo e quelli scritti nelle informazioni qui sotto: un numero
            diverso, se il bot se lo inventasse, viene sostituito con questo prima di partire.
          </p>
          <Campo
            etichetta="Orari della segreteria"
            placeholder="Tutti i giorni 9–13 e 15–20"
            value={m.orari}
            onChange={(e) => cambia((x) => ({ ...x, orari: e.target.value }))}
          />
          <Campo
            etichetta="WhatsApp della segreteria"
            inputMode="tel"
            placeholder="+39 333 1234567"
            value={m.whatsapp}
            onChange={(e) => cambia((x) => ({ ...x, whatsapp: e.target.value }))}
          />
          {whatsappStorto && (
            <p className="guasto">
              Scrivilo col prefisso del paese, per esempio +39 333 1234567: senza, WhatsApp aprirebbe la chat
              con un altro numero.
            </p>
          )}
          <p className="tenue piccolo">
            Se c’è, sotto ogni messaggio che manda in segreteria il bot mette il bottone «💬 Contatta la
            segreteria su WhatsApp». Mettilo solo se su quel numero WhatsApp lo legge una persona; vuoto, il
            bottone non compare e resta il numero di telefono scritto.
          </p>
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>Le regole</h3>
          <Campo
            etichetta="Fino a quanti giorni avanti si prenota"
            inputMode="numeric"
            placeholder="14"
            value={m.giorni}
            onChange={(e) => cambia((x) => ({ ...x, giorni: e.target.value }))}
          />
          {giorni === undefined && <p className="guasto">Scrivi un numero di giorni, per esempio 14.</p>}
          <Campo
            etichetta="Quante ore prima della partita si può ancora disdire"
            inputMode="numeric"
            placeholder="24"
            value={m.ore}
            onChange={(e) => cambia((x) => ({ ...x, ore: e.target.value }))}
          />
          {ore === undefined && <p className="guasto">Scrivi un numero di ore, per esempio 24.</p>}
          <p className="tenue piccolo">
            Vuote, il bot non elenca campi oltre la finestra e non avvisa della scadenza della disdetta:
            meglio nessun avviso che uno costruito su una regola che nessuno ha deciso.
          </p>
          {/* 📄 Voce 37: l'informativa si scrive e pubblica in «Documenti del bot», e l'indirizzo arriva da lì. */}
          <div className="campo informativa-dai-documenti">
            <span>Informativa privacy</span>
            <span className="indirizzo">
              {m.informativa || <span className="tenue">non ancora pubblicata</span>}
            </span>
            <span className="tenue piccolo">
              Si scrive in{' '}
              <Link to="/impostazioni/documenti?tipo=privacy">Documenti del bot → Informativa privacy</Link>,
              e l’indirizzo si riempie da solo quando la pubblichi.
            </span>
          </div>
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>Le informazioni sul circolo</h3>
          <p className="tenue">
            Il bot le dà a chi le chiede, a parole o dal bottone «Domande e info». Ogni argomento ha due
            caselle: il <strong>testo per il socio</strong>, che il bot riporta, e gli{' '}
            <strong>appunti</strong> per chi li mantiene, che al bot <strong>non arrivano mai</strong>.
          </p>
          {ARGOMENTI_DEL_CIRCOLO.map((a) => (
            <div key={a.chiave} className="argomento-bot">
              <h4>{a.titolo}</h4>
              <label className="campo">
                <span>Testo per il socio</span>
                <textarea
                  rows={3}
                  placeholder={a.aiuto}
                  value={m.argomenti[a.chiave].testo}
                  onChange={(e) => argomento(a.chiave, 'testo', e.target.value)}
                />
              </label>
              <label className="campo">
                <span>Appunti (restano qui, il bot non li vede)</span>
                <textarea
                  rows={2}
                  value={m.argomenti[a.chiave].appunti}
                  onChange={(e) => argomento(a.chiave, 'appunti', e.target.value)}
                />
              </label>
            </div>
          ))}
        </fieldset>

        <div className="riquadro">
          <h3>La scala dei livelli</h3>
          {fasce.length === 0 ? (
            <p className="tenue">
              Non dichiarata: finché non c’è, il bot non dice nessun livello a parole. Da questa schermata non
              si cambia, e salvando resta com’è.
            </p>
          ) : (
            <p className="tenue">
              {fasce.map((f) => f.parola).join(' · ')}. Da questa schermata non si cambia, e salvando resta
              com’è.
            </p>
          )}
        </div>

        {modificabile && (
          <>
            <Guasto errore={salva.error} />
            {salvato && !salva.isPending && (
              <p className="tenue">Salvato. Il bot lo dice da subito (al più entro mezzo minuto).</p>
            )}
            <button type="submit" disabled={salva.isPending || !pronta}>
              {salva.isPending ? 'Un attimo…' : 'Salva'}
            </button>
          </>
        )}
      </form>
    </section>
  );
}
